import { Grid, Typography } from '@material-ui/core'
import { navigate } from 'gatsby'
import { TFunction } from 'i18next'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import searchIcon from '../assets/images/search.svg'
import CHRContentContainer from '../components/contentContainer'
import CHRInput from '../components/input'
import CHROutlinedButton from '../components/outlinedButton'
import SEO from '../components/seo'
import TheChirpyestCard from '../components/theChirpyestCard'
import Layout from '../layouts/defaultLayout'
import { styles } from '../pagesStyles/theChirpyestStyle'
import Breadcrumbs from '@material-ui/core/Breadcrumbs'
import Links from '@material-ui/core/Link'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import { useLocation } from '@reach/router'
import debounce from 'lodash.debounce'
import { parse } from 'query-string'
import axios from '../axios'
import CHRSpinner from '../components/spinner'
import { CSEvents, ENDPOINTS, ROUTES, ShoppingBoardView } from '../constants'
import { tracker } from '../systemLogs'
import {
  getQueryString,
  TheChirpyestUser,
  UsersResultsState,
} from '../types/theChirpyest'
import { createListSeed } from '../../utils/randomIdGenerator'
import NotFound from './404'

interface TheChirpyestProps {
  t: TFunction
}

interface TheChirpyestApiResponse {
  data: TheChirpyestUser[]
}

const DEFAULT_SIZE = 30

const createInitialResults = (): UsersResultsState => ({
  data: [],
  finishedLoading: true,
  offset: 0,
  size: DEFAULT_SIZE,
})

const TheChirpyest = ({ t }: TheChirpyestProps) => {
  const classes = styles()
  const { search, pathname } = useLocation()
  const { q } = parse(search)
  const [usersResults, setUsersResults] = useState<UsersResultsState>(
    createInitialResults
  )
  const [searchText, setSearchText] = useState(getQueryString(q))
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [showScrollBtn, setShowScrollBtn] = useState(false)
  const [listSeed, setListSeed] = useState(createListSeed)
  const getUsersResultsRef = useRef<() => Promise<void>>(async () => {})

  const getUsersResults = useCallback(async () => {
    try {
      const {
        data: { data: results },
      } = await axios.get<TheChirpyestApiResponse>(
        `${ENDPOINTS.getTheChirpyest}?offset=${usersResults.offset}&size=${
          usersResults.size
        }&q=${encodeURIComponent(searchText)}&seed=${encodeURIComponent(
          listSeed
        )}`
      )
      setUsersResults(prev => ({
        finishedLoading: results.length === 0 || results.length < prev.size,
        offset: prev.offset + prev.size,
        size: DEFAULT_SIZE,
        data: prev.data.concat(results),
      }))
    } catch (err) {
      setError(t('messages.somethingWentWrong'))
    }

    setIsLoading(false)
  }, [listSeed, searchText, t, usersResults.offset, usersResults.size])

  getUsersResultsRef.current = getUsersResults

  // Keeps the address bar in sync with the settled search term so refresh and
  // browser back/forward can rehydrate `searchText` from the URL instead of
  // losing it. `replace: true` avoids pushing a new history entry per search
  // (matches the debounce boundary in fetchResults, not every keystroke).
  const syncSearchParam = useCallback(
    (query: string) => {
      const params = new URLSearchParams(search)
      if (query) {
        params.set('q', query)
      } else {
        params.delete('q')
      }
      const queryString = params.toString()
      navigate(`${pathname}${queryString ? `?${queryString}` : ''}`, {
        replace: true,
      })
    },
    [pathname, search]
  )

  const fetchResults = useCallback(
    async (query: string) => {
      syncSearchParam(query)
      const newSeed = createListSeed()
      setListSeed(newSeed)
      try {
        const {
          data: { data: results },
        } = await axios.get<TheChirpyestApiResponse>(
          `${ENDPOINTS.getTheChirpyest}?offset=0&size=${
            usersResults.size
          }&q=${encodeURIComponent(query)}&seed=${encodeURIComponent(newSeed)}`
        )
        setUsersResults({
          finishedLoading:
            results.length === 0 || results.length < usersResults.size,
          offset: usersResults.size,
          size: DEFAULT_SIZE,
          data: results,
        })
      } catch (err) {
        setError(t('messages.somethingWentWrong'))
      }
    },
    [syncSearchParam, t, usersResults.size]
  )

  const debouncedSave = useMemo(
    () =>
      debounce((query: string) => {
        void fetchResults(query)
      }, 300),
    [fetchResults]
  )

  useEffect(() => {
    return () => {
      debouncedSave.cancel()
    }
  }, [debouncedSave])

  useEffect(() => {
    const prevState = sessionStorage.getItem('the-chirpyest')
    const prevQuery = sessionStorage.getItem('the-chirpyest-query')
    const currentQuery = getQueryString(q)
    // Only restore if the saved snapshot was produced by the search term
    // still in the URL right now - i.e. this is actually a browser back/
    // forward return to that same search, not a fresh visit (e.g. clicking
    // "Community" in the header) that happens to land while a stale
    // snapshot from an earlier search is still sitting in sessionStorage.
    const canRestore = prevState !== null && prevQuery === currentQuery

    if (canRestore) {
      setUsersResults(JSON.parse(prevState as string) as UsersResultsState)
      setSearchText(currentQuery)
      setIsLoading(false)

      const savedSeed = sessionStorage.getItem('the-chirpyest-seed')
      if (savedSeed) {
        setListSeed(savedSeed)
      }

      const scrollPos = sessionStorage.getItem('the-chirpyest-scroll-pos')
      setTimeout(() => window.scrollTo(0, Number(scrollPos ?? 0)), 100)
    }

    // Always clear - a snapshot must never survive past the mount it was
    // meant for, whether it was just consumed above or left stale because
    // the search term no longer matches.
    sessionStorage.removeItem('the-chirpyest')
    sessionStorage.removeItem('the-chirpyest-query')
    sessionStorage.removeItem('the-chirpyest-seed')
    sessionStorage.removeItem('the-chirpyest-scroll-pos')

    if (!canRestore) {
      void getUsersResultsRef.current()
    }
  }, [])

  useEffect(() => {
    window.onscroll = () => {
      const minScroll = 150
      if (
        document.body.scrollTop >= minScroll ||
        document.documentElement.scrollTop >= minScroll
      ) {
        setShowScrollBtn(true)
      } else {
        setShowScrollBtn(false)
      }
    }
    return () => {
      window.onscroll = null
    }
  }, [])

  const updateValue = (query: string) => {
    setSearchText(query)
    debouncedSave(query)
  }

  const goToShoppingBoard = (
    username: string,
    defaultShoppingBoardView?: ShoppingBoardView
  ) => {
    sessionStorage.setItem('the-chirpyest', JSON.stringify(usersResults))
    sessionStorage.setItem('the-chirpyest-query', searchText)
    sessionStorage.setItem('the-chirpyest-seed', listSeed)
    sessionStorage.setItem('the-chirpyest-scroll-pos', `${window.scrollY}`)
    let link = `/s/${username}`
    if (defaultShoppingBoardView === ShoppingBoardView.ALL_FOLDERS) {
      link += '#folders'
    }
    navigate(link)
  }

  const scrollUp = () => {
    window.scroll({
      top: 0,
      behavior: 'smooth',
    })
  }

  const trackClickStream = async (username: string) => {
    await tracker.trackCs(CSEvents.viewBoard, {
      pageUrl: window.location.href,
      shoppingBoardUsername: username,
      section: 'chirpyest-community',
    })
  }

  if (error) {
    return <NotFound error={error} />
  }

  return (
    <Layout>
      <SEO
        title={t('seo.theChirpyest.title')}
        description={t('seo.theChirpyest.description')}
        keywords={t('seo.theChirpyest.keywords')}
      />
      <CHRContentContainer>
        <div className={classes.header}>
          <div className={classes.leftSide}>
            <Breadcrumbs aria-label="breadcrumb">
              <Links
                color="inherit"
                rel="preconnect"
                onClick={(
                  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
                ) => {
                  event.preventDefault()
                  navigate(ROUTES.home)
                }}
              >
                home
              </Links>
              <Typography color="textPrimary">chirpyest community</Typography>
            </Breadcrumbs>
            <Typography variant="h4" className={classes.theChirpyest}>
              chirpyest community
            </Typography>
            <Typography
              variant="subtitle1"
              component="p"
              className={classes.description}
            >
              {t('theChirpyest.metaDescription')}
            </Typography>

            <Typography
              component="h1"
              variant="h1"
              className={classes.seoHeading}
            >
              {t('seo.theChirpyest.h1')}
            </Typography>
          </div>
          <div className={classes.headerRightSide}>
            <div className={classes.searchWrapper}>
              <CHRInput
                type="text"
                value={searchText}
                placeholder="search the chirpyest community"
                onChange={e => updateValue(e.target.value)}
              />
              <button className={classes.searchButton} type="button">
                <img
                  src={searchIcon}
                  alt={t('imageAlts.search')}
                  className={classes.searchIcon}
                />
              </button>
            </div>
          </div>
        </div>
        {isLoading ? (
          <CHRSpinner />
        ) : (
          <Grid
            container
            alignItems="stretch"
            spacing={2}
            className={classes.mainGrid}
          >
            {usersResults.data.map(
              ({
                user_id: userId,
                image: userImage,
                items,
                name,
                username,
                short_bio: blurb,
                community_image: communityImage,
                default_shopping_board_view: defaultShoppingBoardView,
                match_type: matchType,
                matched_brand_name: matchedBrandName,
              }) => (
                <Grid
                  item
                  lg={4}
                  md={4}
                  sm={6}
                  xs={12}
                  key={userId}
                  onClick={() => trackClickStream(username)}
                >
                  <TheChirpyestCard
                    userImage={userImage}
                    items={items}
                    description={name}
                    userName={username}
                    blurb={blurb}
                    communityImage={communityImage}
                    searchText={searchText}
                    matchType={matchType}
                    matchedBrandName={matchedBrandName}
                    onClick={() =>
                      goToShoppingBoard(username, defaultShoppingBoardView)
                    }
                  />
                </Grid>
              )
            )}
          </Grid>
        )}
        {!isLoading && !usersResults.data.length && (
          <Typography variant="h3" className={classes.noData}>
            sorry, there is no data
          </Typography>
        )}
        {!usersResults.finishedLoading && (
          <CHROutlinedButton
            label={t('dashboard.loadMore')}
            onClick={() => {
              void getUsersResults()
            }}
          />
        )}
      </CHRContentContainer>
      <button
        onClick={scrollUp}
        className={`${classes.scrollButton} ${
          showScrollBtn ? '' : classes.hidden
        }`}
      >
        <ArrowUpwardIcon fontSize="large" />
      </button>
    </Layout>
  )
}

export default withTranslation()(TheChirpyest)
